import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Activity, Check, ChevronLeft, Copy, Expand, ExternalLink, LayoutDashboard, Loader, Pencil, Save, Shield, SlidersHorizontal, Trash2 } from 'lucide-react';
import PillTabs, { type PillTabItem } from '../../../components/common/PillTabs';
import ConfirmDialog from '../Dialogs/ConfirmDialog';
import EmptyState from '../governed-mcp-gateway/components/EmptyState';
import ChatAgentSecurity from '../chatagents/new/ChatAgentSecurity';
import { dangerBtn, fieldClass, GatewayDrawer, ghostBtn, panelClass, primaryBtn, ToggleRow } from '../governed-mcp-gateway/components/GatewayUi';
import OmniChatAdvancedTab from './OmniChatAdvancedTab';
import OmniChatGatewayFields from './OmniChatGatewayFields';
import OmniChatMonitoringTab from './OmniChatMonitoringTab';
import { omniChatApi } from './omniChatApi';
import { coercePolicies, formFromAgent, liveChatUrl, OMNICHAT_PATHS, OMNICHAT_SECURITY_MODULE_ID, omniChatIcon } from './omniChatConstants';
import { logOmniChat } from './omniChatLogger';
import { useOmniChatPage } from './OmniChatScreen';
import { useOmniChat } from './omniChatStore';
import { getAgentMonitor } from './omniChatSynthetic';
import type { OmniChatAgent, OmniChatAuditEvent, OmniChatFormState } from './omniChatTypes';

type DetailTab = 'overview' | 'monitoring' | 'security' | 'advanced';

const TABS: PillTabItem<DetailTab>[] = [
  { id: 'overview', label: 'Overview', icon: LayoutDashboard },
  { id: 'monitoring', label: 'Monitoring', icon: Activity },
  { id: 'security', label: 'Security', icon: Shield },
  { id: 'advanced', label: 'Advanced', icon: SlidersHorizontal },
];

function formatWhen(value: string): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

function creatorLabel(agent: OmniChatAgent): string {
  return agent.createdByName || agent.createdByEmail || agent.createdBy || 'Unknown';
}

function actorLabel(event: OmniChatAuditEvent): string {
  return event.actorName || event.actorEmail || event.actorId || 'Unknown';
}

function LiveLink({ url, isLight }: { url: string; isLight: boolean }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
      logOmniChat('app', 'live_link_copied');
    } catch {
      logOmniChat('warning', 'live_link_copy_failed');
    }
  };

  return (
    <div className="flex items-center gap-2 min-w-0 w-full">
      <span
        className={`min-w-0 flex-1 truncate rounded-lg px-2.5 py-1.5 text-[12px] ${
          isLight ? 'bg-neutral-100 text-neutral-600' : 'bg-neutral-800 text-neutral-300'
        }`}
        title={url}
      >
        {url}
      </span>
      <button type="button" className={`${ghostBtn(isLight)} h-8 px-2`} onClick={() => void copy()} aria-label="Copy live link">
        {copied ? <Check size={13} /> : <Copy size={13} />}
      </button>
      <a className={`${ghostBtn(isLight)} h-8 px-2`} href={url} target="_blank" rel="noreferrer" aria-label="Open live chat">
        <ExternalLink size={13} />
      </a>
    </div>
  );
}

export default function OmniChatDetailPage() {
  const { isLight, onToast } = useOmniChatPage();
  const { agentId } = useParams<{ agentId: string }>();
  const navigate = useNavigate();
  const { getAgent, ready, saveAgent, findGateway, setActive, removeAgent, assistantUrl } = useOmniChat();
  const agent = agentId ? getAgent(agentId) : undefined;
  const [tab, setTab] = useState<DetailTab>('overview');
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [form, setForm] = useState<OmniChatFormState | null>(null);
  const [activityTick, setActivityTick] = useState(0);

  useEffect(() => {
    if (agent) logOmniChat('app', 'detail_open', { id: agent.id });
  }, [agent]);

  useEffect(() => {
    setEditing(false);
    setForm(null);
    setConfirmDelete(false);
  }, [agent?.id]);

  if (!ready) return null;

  if (!agent) {
    return (
      <EmptyState
        isLight={isLight}
        title="Agent not found"
        description="This agent is missing or was removed."
        action={
          <button type="button" className={primaryBtn()} onClick={() => navigate(OMNICHAT_PATHS.list)}>
            Back to OmniChat
          </button>
        }
      />
    );
  }

  const gateway = findGateway(agent.gatewayId);
  const Icon = omniChatIcon(agent.iconId);
  const ink = isLight ? 'text-neutral-900' : 'text-white';
  const muted = isLight ? 'text-neutral-500' : 'text-neutral-400';
  const link = liveChatUrl(agent.id, assistantUrl);
  const draft = form ?? formFromAgent(agent);

  const startEdit = () => {
    setForm(formFromAgent(agent));
    if (tab === 'monitoring' || tab === 'security') setTab('overview');
    setEditing(true);
    logOmniChat('app', 'review_edit_start', { id: agent.id, tab });
  };

  const cancelEdit = () => {
    setForm(formFromAgent(agent));
    setEditing(false);
    logOmniChat('app', 'review_edit_cancel', { id: agent.id });
  };

  const persist = async () => {
    if (!draft.title.trim() || !draft.instruction.trim()) {
      onToast('error', 'Add a title and instruction before saving.');
      return;
    }
    if (draft.gatewayId && !draft.inferenceQuotaId) {
      onToast('error', 'Select an LLM gateway key before saving.');
      return;
    }
    setSaving(true);
    try {
      await saveAgent({ ...draft, iconId: agent.iconId }, agent.id);
      onToast('success', 'Agent updated.');
      setEditing(false);
      setForm(null);
      setActivityTick((tick) => tick + 1);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Could not save this agent.';
      logOmniChat('error', 'review_save_failed', { message });
      onToast('error', message);
    } finally {
      setSaving(false);
    }
  };

  const persistActive = async (active: boolean) => {
    try {
      await setActive(agent.id, active);
      onToast('success', active ? 'Agent activated.' : 'Agent deactivated.');
      setActivityTick((tick) => tick + 1);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Could not update activation.';
      logOmniChat('error', 'activation_failed', { message });
      onToast('error', message);
    }
  };

  const confirmRemove = async () => {
    setConfirmDelete(false);
    try {
      await removeAgent(agent.id);
      onToast('success', 'Agent deleted.');
      navigate(OMNICHAT_PATHS.list);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Could not delete this agent.';
      logOmniChat('error', 'delete_failed', { message });
      onToast('error', message);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3 min-w-0">
          <button
            type="button"
            className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border ${
              isLight
                ? 'border-neutral-200 text-neutral-500 hover:border-neutral-300 hover:text-neutral-800'
                : 'border-white/10 text-neutral-400 hover:border-white/20 hover:text-white'
            }`}
            onClick={() => navigate(OMNICHAT_PATHS.list)}
            aria-label="Back to agents"
          >
            <ChevronLeft size={16} />
          </button>
          <span className={`h-11 w-11 rounded-xl flex items-center justify-center ${isLight ? 'bg-indigo-50 text-indigo-600' : 'bg-indigo-500/15 text-indigo-300'}`}>
            <Icon size={20} />
          </span>
          <div className="min-w-0">
            {editing ? (
              <input
                className={`${fieldClass(isLight)} h-9 py-0 text-base font-semibold`}
                value={draft.title}
                placeholder="Agent title"
                onChange={(event) => setForm((prev) => ({ ...(prev ?? formFromAgent(agent)), title: event.target.value }))}
              />
            ) : (
              <h1 className={`text-xl font-semibold truncate ${ink}`}>{agent.title}</h1>
            )}
            <p className={`text-[13px] truncate ${muted}`}>{gateway?.name ?? 'No gateway'} · {gateway?.model ?? 'Unassigned'}</p>
          </div>
        </div>
        {editing ? (
          <div className="flex items-center gap-2">
            <button type="button" className={ghostBtn(isLight)} onClick={cancelEdit}>
              Cancel
            </button>
            <button type="button" className={primaryBtn()} disabled={saving} onClick={persist}>
              {saving ? <Loader size={14} className="animate-spin" /> : <Save size={14} />}
              Save
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <button type="button" className={dangerBtn()} onClick={() => setConfirmDelete(true)}>
              <Trash2 size={14} />
              Delete
            </button>
            <button type="button" className={primaryBtn()} onClick={startEdit}>
              <Pencil size={14} />
              Edit
            </button>
          </div>
        )}
      </div>

      <PillTabs
        isLight={isLight}
        ariaLabel="Agent sections"
        value={tab}
        items={TABS}
        onChange={(next) => {
          setTab(next);
          logOmniChat('app', 'detail_tab', { id: agent.id, tab: next });
        }}
      />

      {tab === 'overview' ? (
        <OverviewTab
          agent={agent}
          isLight={isLight}
          liveUrl={link}
          editing={editing}
          form={draft}
          activityTick={activityTick}
          onActiveChange={(next) => void persistActive(next)}
          onPatch={(next) => setForm((prev) => ({ ...(prev ?? formFromAgent(agent)), ...next }))}
        />
      ) : null}
      {tab === 'monitoring' ? (
        <OmniChatMonitoringTab
          agentId={agent.id}
          gatewayId={agent.gatewayId}
          insight={getAgentMonitor(agent.id, agent.connectorIds)}
          isLight={isLight}
        />
      ) : null}
      {tab === 'security' ? (
        <div className={`${panelClass(isLight)} overflow-hidden min-h-[28rem]`}>
          <ChatAgentSecurity
            theme={isLight ? 'light' : 'dark'}
            objectId={agent.id}
            moduleId={OMNICHAT_SECURITY_MODULE_ID}
            entityName="OmniChat Agent"
            hideHeader
          />
        </div>
      ) : null}
      {tab === 'advanced' ? (
        <OmniChatAdvancedTab
          policies={coercePolicies(editing ? draft.policies : agent.policies)}
          editing={editing}
          isLight={isLight}
          onChange={(policies) => setForm((prev) => ({ ...(prev ?? formFromAgent(agent)), policies }))}
        />
      ) : null}

      {confirmDelete ? (
        <ConfirmDialog
          title="Delete agent?"
          message={`This permanently removes ${agent.title}. This cannot be undone.`}
          onYes={() => void confirmRemove()}
          onCancel={() => setConfirmDelete(false)}
        />
      ) : null}
    </div>
  );
}

function OverviewTab({
  agent,
  isLight,
  liveUrl,
  editing,
  form,
  activityTick,
  onActiveChange,
  onPatch,
}: {
  agent: OmniChatAgent;
  isLight: boolean;
  liveUrl: string;
  editing: boolean;
  form: OmniChatFormState;
  activityTick: number;
  onActiveChange: (next: boolean) => void;
  onPatch: (next: Partial<OmniChatFormState>) => void;
}) {
  const { gateways, inferenceKeys, findGateway } = useOmniChat();
  const gateway = findGateway(editing ? form.gatewayId : agent.gatewayId);
  const selectedKey = inferenceKeys.find((item) => item.id === (editing ? form.inferenceQuotaId : agent.inferenceQuotaId));
  const ink = isLight ? 'text-neutral-900' : 'text-white';
  const muted = isLight ? 'text-neutral-500' : 'text-neutral-400';

  return (
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
      <section className={`${panelClass(isLight)} p-6 xl:col-span-2 space-y-6`}>
        <div>
          <p className={`text-[11px] font-semibold uppercase tracking-wide ${muted}`}>Description</p>
          {editing ? (
            <textarea
              className={`${fieldClass(isLight)} mt-2 min-h-[88px] resize-y`}
              value={form.description}
              placeholder="What this agent is for."
              onChange={(event) => onPatch({ description: event.target.value })}
            />
          ) : (
            <p className={`mt-2 text-sm leading-7 ${ink}`}>{agent.description || 'No description yet.'}</p>
          )}
        </div>
        <div className={`pt-5 border-t ${isLight ? 'border-neutral-100' : 'border-neutral-800'}`}>
          <p className={`text-[11px] font-semibold uppercase tracking-wide ${muted}`}>Instruction</p>
          {editing ? (
            <textarea
              className={`${fieldClass(isLight)} mt-2 min-h-[140px] resize-y`}
              value={form.instruction}
              placeholder="You help employees with…"
              onChange={(event) => onPatch({ instruction: event.target.value })}
            />
          ) : (
            <p className={`mt-2 text-sm leading-7 ${isLight ? 'text-neutral-700' : 'text-neutral-300'}`}>{agent.instruction}</p>
          )}
        </div>
      </section>
      <div className="space-y-4">
        <section className={`${panelClass(isLight)} p-5 space-y-5`}>
          <ToggleRow
            label={agent.active ? 'Active' : 'Inactive'}
            checked={agent.active}
            onChange={onActiveChange}
            isLight={isLight}
          />
          {editing ? (
            <OmniChatGatewayFields
              form={form}
              gateways={gateways}
              inferenceKeys={inferenceKeys}
              isLight={isLight}
              onPatch={onPatch}
            />
          ) : (
            <div>
              <p className={`text-[11px] font-semibold uppercase tracking-wide ${muted}`}>LLM Gateway</p>
              <p className={`mt-1.5 text-sm font-medium ${ink}`}>{gateway?.name ?? 'Unassigned'}</p>
              <p className={`text-[12px] ${muted}`}>{gateway?.model ?? '—'}</p>
              <p className={`text-[12px] mt-1 ${muted}`}>
                {agent.inferenceKeyLast4
                  ? `${selectedKey?.clientName ? `${selectedKey.clientName} · ` : ''}••••${agent.inferenceKeyLast4}`
                  : 'No key assigned'}
              </p>
            </div>
          )}
          <div className={`text-[11px] space-y-0.5 ${muted}`}>
            <p>Updated {formatWhen(agent.updatedAt) || '—'}</p>
            <p>Created by {creatorLabel(agent)}</p>
            {agent.createdAt ? <p>{formatWhen(agent.createdAt)}</p> : null}
          </div>
        </section>
        <section className={`${panelClass(isLight)} p-5 space-y-2`}>
          <p className={`text-[11px] font-semibold uppercase tracking-wide ${muted}`}>Live chat link</p>
          <LiveLink url={liveUrl} isLight={isLight} />
        </section>
        <ActivityCard agentId={agent.id} isLight={isLight} refreshKey={activityTick} />
      </div>
    </div>
  );
}

function ActivityRow({ event, ink, muted }: { event: OmniChatAuditEvent; ink: string; muted: string }) {
  return (
    <li className="min-w-0">
      <p className={`text-sm capitalize ${ink}`}>{event.action}</p>
      <p className={`text-[11px] truncate ${muted}`}>
        {actorLabel(event)}
        {event.occurredAt ? ` · ${formatWhen(event.occurredAt)}` : ''}
      </p>
      {event.summary ? <p className={`mt-0.5 text-[12px] ${muted}`}>{event.summary}</p> : null}
    </li>
  );
}

function ActivityList({
  items,
  status,
  ink,
  muted,
  withSummary,
}: {
  items: OmniChatAuditEvent[];
  status: 'loading' | 'ok' | 'empty' | 'error';
  ink: string;
  muted: string;
  withSummary?: boolean;
}) {
  if (status === 'loading') return <p className={`text-sm ${muted}`}>Loading activity…</p>;
  if (status === 'error') return <p className={`text-sm ${muted}`}>Activity is unavailable.</p>;
  if (status === 'empty') return <p className={`text-sm ${muted}`}>No activity yet.</p>;
  return (
    <ul className="space-y-2.5">
      {items.map((event) =>
        withSummary ? (
          <ActivityRow key={event.eventId} event={event} ink={ink} muted={muted} />
        ) : (
          <li key={event.eventId} className="min-w-0">
            <p className={`text-sm capitalize ${ink}`}>{event.action}</p>
            <p className={`text-[11px] truncate ${muted}`}>
              {actorLabel(event)}
              {event.occurredAt ? ` · ${formatWhen(event.occurredAt)}` : ''}
            </p>
          </li>
        )
      )}
    </ul>
  );
}

function ActivityCard({ agentId, isLight, refreshKey }: { agentId: string; isLight: boolean; refreshKey: number }) {
  const muted = isLight ? 'text-neutral-500' : 'text-neutral-400';
  const ink = isLight ? 'text-neutral-800' : 'text-neutral-200';
  const [items, setItems] = useState<OmniChatAuditEvent[]>([]);
  const [status, setStatus] = useState<'loading' | 'ok' | 'empty' | 'error'>('loading');
  const [open, setOpen] = useState(false);
  const [allItems, setAllItems] = useState<OmniChatAuditEvent[]>([]);
  const [allStatus, setAllStatus] = useState<'loading' | 'ok' | 'empty' | 'error'>('loading');

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    void omniChatApi
      .listAudit(agentId)
      .then((events) => {
        if (cancelled) return;
        setItems(events);
        setStatus(events.length ? 'ok' : 'empty');
      })
      .catch(() => {
        if (cancelled) return;
        setItems([]);
        setStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [agentId, refreshKey]);

  const openAll = () => {
    setOpen(true);
    setAllStatus('loading');
    logOmniChat('app', 'audit_review_open', { id: agentId });
    void omniChatApi
      .listAudit(agentId, 50)
      .then((events) => {
        setAllItems(events);
        setAllStatus(events.length ? 'ok' : 'empty');
      })
      .catch(() => {
        setAllItems([]);
        setAllStatus('error');
      });
  };

  return (
    <section className={`${panelClass(isLight)} p-5 space-y-3`}>
      <div className="flex items-center justify-between gap-2">
        <p className={`text-[11px] font-semibold uppercase tracking-wide ${muted}`}>Activity</p>
        <button
          type="button"
          className={`inline-flex h-6 w-6 items-center justify-center rounded-md ${
            isLight ? 'text-neutral-400 hover:text-neutral-700' : 'text-neutral-500 hover:text-neutral-200'
          }`}
          onClick={openAll}
          aria-label="Review all activity"
        >
          <Expand size={13} />
        </button>
      </div>
      <ActivityList items={items.slice(0, 4)} status={status} ink={ink} muted={muted} />
      <GatewayDrawer
        open={open}
        title="Activity"
        subtitle="All changes for this agent"
        icon={<Activity size={18} />}
        isLight={isLight}
        size="lg"
        widthClass="w-full max-w-xl"
        onClose={() => setOpen(false)}
      >
        <ActivityList items={allItems} status={allStatus} ink={ink} muted={muted} withSummary />
      </GatewayDrawer>
    </section>
  );
}
